import { Box, styled } from "@mui/material";

import {
  ANDROID_PHONE_HEIGHT,
  ANDROID_PHONE_WIDTH,
  AndroidPhone,
} from "./android/AndroidPhone";
import { IOS_PHONE_HEIGHT, IOS_PHONE_WIDTH, IosPhone } from "./ios/IosPhone";
import type { PhoneMessage, PhoneScreen } from "./phoneMessage";
import { ScaledFrame } from "./ScaledFrame";
import { usePhoneFitScale } from "./usePhoneFitScale";

import { AndroidLogoIcon, AppleLogoIcon } from "@/icons";

const GAP = 24;
const CAPTION_SX = { color: "text.contrastText", fontSize: 22 } as const;
const CAPTION_HEIGHT = 32;
// Render both phones at the same height so the pair reads as one size. Real
// device sizes differ (Pixel is taller), so shrink each to a shared target.
const TARGET_HEIGHT = Math.min(IOS_PHONE_HEIGHT, ANDROID_PHONE_HEIGHT);
const IOS_NORM = TARGET_HEIGHT / IOS_PHONE_HEIGHT;
const ANDROID_NORM = TARGET_HEIGHT / ANDROID_PHONE_HEIGHT;
const PAIR_WIDTH =
  IOS_PHONE_WIDTH * IOS_NORM + GAP + ANDROID_PHONE_WIDTH * ANDROID_NORM;
const PAIR_HEIGHT = TARGET_HEIGHT + CAPTION_HEIGHT;

const Row = styled(Box)({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: GAP,
});

const Column = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
}));

interface PhonePairProps {
  message: PhoneMessage;
  screen?: PhoneScreen;
  onRetry?: () => void;
}

export function PhonePair({ message, screen, onRetry }: PhonePairProps) {
  const { ref, scale } = usePhoneFitScale({
    width: PAIR_WIDTH,
    height: PAIR_HEIGHT,
  });
  return (
    <Row ref={ref}>
      <Column>
        <ScaledFrame
          width={IOS_PHONE_WIDTH}
          height={IOS_PHONE_HEIGHT}
          scale={scale * IOS_NORM}
        >
          <IosPhone message={message} screen={screen} onRetry={onRetry} />
        </ScaledFrame>
        <AppleLogoIcon
          role="img"
          aria-label="iPhone 16, iOS 18"
          sx={CAPTION_SX}
        />
      </Column>
      <Column>
        <ScaledFrame
          width={ANDROID_PHONE_WIDTH}
          height={ANDROID_PHONE_HEIGHT}
          scale={scale * ANDROID_NORM}
        >
          <AndroidPhone message={message} screen={screen} onRetry={onRetry} />
        </ScaledFrame>
        <AndroidLogoIcon
          role="img"
          aria-label="Pixel 9, Android 15"
          sx={CAPTION_SX}
        />
      </Column>
    </Row>
  );
}
